import React, { useState, useEffect, useRef } from 'react';
import { 
  FaGraduationCap, FaUniversity, FaRupeeSign,
  FaBookOpen, FaChartLine, FaFlask, FaLandmark,
  FaCheckCircle, FaStar, FaDownload,
  FaPrint, FaShare
} from 'react-icons/fa';

const FeeStructure = () => {
  const [activeTab, setActiveTab] = useState('graduation');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const graduationData = [
    { 
      course: "Bachelor of Arts", 
      shortName: "B.A.", 
      year1: "₹ 5,500/-", 
      year2: "₹ 5,500/-", 
      year3: "₹ 5,500/-",
      total: "₹ 16,500/-",
      icon: <FaLandmark />
    },
    { 
      course: "Bachelor of Commerce", 
      shortName: "B.Com.", 
      year1: "₹ 10,500/-", 
      year2: "₹ 10,500/-", 
      year3: "₹ 10,500/-",
      total: "₹ 31,500/-",
      icon: <FaChartLine />
    },
    { 
      course: "Bachelor of Science", 
      shortName: "B.Sc.", 
      year1: "₹ 14,500/-", 
      year2: "₹ 14,500/-", 
      year3: "₹ 14,500/-",
      total: "₹ 43,500/-",
      icon: <FaFlask />
    }
  ];

  const postGraduationData = [
    { 
      course: "Master of Arts (Sociology)", 
      shortName: "M.A. Sociology", 
      year1: "₹ 9,000/-", 
      year2: "₹ 9,000/-", 
      total: "₹ 18,000/-",
      icon: <FaBookOpen />
    },
    { 
      course: "Master of Arts (Home Science)", 
      shortName: "M.A. Home Science", 
      year1: "₹ 16,000/-", 
      year2: "₹ 16,000/-", 
      total: "₹ 32,000/-",
      icon: <FaBookOpen />
    },
    { 
      course: "Master of Arts (Education)", 
      shortName: "M.A. Education", 
      year1: "₹ 12,000/-", 
      year2: "₹ 12,000/-", 
      total: "₹ 24,000/-",
      icon: <FaBookOpen />
    },
    { 
      course: "Master of Science", 
      shortName: "M.Sc.", 
      year1: "₹ 20,000/-", 
      year2: "₹ 20,000/-", 
      total: "₹ 40,000/-",
      icon: <FaFlask />
    }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16 min-h-screen">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Heading */}
          <div className={`text-center mb-10 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 rounded-2xl shadow-lg">
                <FaRupeeSign className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="text-blue-800">
                Fee Structure
              </span>
            </h2>
            <div className="w-24 h-1 bg-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4">
              Affordable fee structure for quality education
            </p>
          </div>

          {/* Tab Buttons */}
          <div className={`flex justify-center gap-4 mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <button
              onClick={() => setActiveTab('graduation')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                activeTab === 'graduation'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-600 hover:bg-blue-50'
              }`}
            >
              <FaGraduationCap />
              Graduation
            </button>
            <button
              onClick={() => setActiveTab('postgraduation')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                activeTab === 'postgraduation'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-600 hover:bg-blue-50'
              }`}
            >
              <FaUniversity />
              Post Graduation
            </button>
          </div>

          {/* Graduation Table */}
          {activeTab === 'graduation' && (
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden transition-all duration-500">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4">
                <h3 className="text-white font-bold text-xl text-center flex items-center justify-center gap-2">
                  <FaGraduationCap />
                  Graduation Courses Fee Structure
                </h3>
              </div>
              
              <div className="overflow-x-auto p-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="p-3 text-left font-bold text-blue-800 border">Course</th>
                      <th className="p-3 text-center font-bold text-blue-800 border">I Year</th>
                      <th className="p-3 text-center font-bold text-blue-800 border">II Year</th>
                      <th className="p-3 text-center font-bold text-blue-800 border">III Year</th>
                      <th className="p-3 text-center font-bold text-blue-800 border">Total Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {graduationData.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-all">
                        <td className="p-3 border">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                              {item.icon}
                            </div>
                            <span className="font-semibold text-gray-800">{item.course}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center border text-gray-600">{item.year1}</td>
                        <td className="p-3 text-center border text-gray-600">{item.year2}</td>
                        <td className="p-3 text-center border text-gray-600">{item.year3}</td>
                        <td className="p-3 text-center border font-bold text-blue-600">{item.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Post Graduation Table */}
          {activeTab === 'postgraduation' && (
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden transition-all duration-500">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4">
                <h3 className="text-white font-bold text-xl text-center flex items-center justify-center gap-2">
                  <FaUniversity />
                  Post Graduation Courses Fee Structure
                </h3>
              </div>
              
              <div className="overflow-x-auto p-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="p-3 text-left font-bold text-blue-800 border">Course</th>
                      <th className="p-3 text-center font-bold text-blue-800 border">I Year</th>
                      <th className="p-3 text-center font-bold text-blue-800 border">II Year</th>
                      <th className="p-3 text-center font-bold text-blue-800 border">Total Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {postGraduationData.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-all">
                        <td className="p-3 border">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                              {item.icon}
                            </div>
                            <span className="font-semibold text-gray-800">{item.course}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center border text-gray-600">{item.year1}</td>
                        <td className="p-3 text-center border text-gray-600">{item.year2}</td>
                        <td className="p-3 text-center border font-bold text-blue-600">{item.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Important Notes */}
          <div className={`mt-8 bg-blue-50 rounded-2xl p-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FaStar className="text-blue-500" />
              Important Notes:
            </h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <FaCheckCircle className="text-blue-500 text-xs mt-0.5" />
                Fees are subject to change as per college norms
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <FaCheckCircle className="text-blue-500 text-xs mt-0.5" />
                Admission Form Fee: ₹500/- (One Time)
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <FaCheckCircle className="text-blue-500 text-xs mt-0.5" />
                Scholarships available for meritorious and economically weaker students
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <FaCheckCircle className="text-blue-500 text-xs mt-0.5" />
                Fees can be paid online or offline
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className={`flex flex-wrap justify-center gap-4 mt-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <button className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white px-5 py-2 rounded-xl font-semibold text-sm hover:shadow-lg transition-all">
              <FaDownload />
              Download Fee Structure
            </button>
            <button className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white px-5 py-2 rounded-xl font-semibold text-sm hover:shadow-lg transition-all">
              <FaPrint />
              Print Fee Details
            </button>
            <button className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white px-5 py-2 rounded-xl font-semibold text-sm hover:shadow-lg transition-all">
              <FaShare />
              Share
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default FeeStructure;